/**
 * APP-201: the wording a letter prints where its template asks for the award's wording
 * (`publicCitation`; the client letters' `Reason` / `AwardDescription`). One wording for everyone
 * on the award: what the person entering it typed, or as the Stock Admin team changed it; else the
 * award's name; else the program's name (APP-270, Tom on FB-2026-0032, reversing APP-201's order).
 * Never the write-up or the justification, which can hold confidential reasons.
 */

import { withoutHidden } from './feedback/index.js';

/** Where the award's wording comes from, as the entry form and the letters step say it. */
export type LetterWordingSource = 'entered' | 'program' | 'title' | 'none';

export interface LetterWording {
  /** Null when there's nothing to print yet: no program, no name, nothing typed. */
  readonly text: string | null;
  readonly source: LetterWordingSource;
}

/**
 * The words as typed, trimmed; null when they'd show as nothing on the letter, whether blank or only
 * invisible characters (SENT-256). A joiner inside a real name stays: Persian, Bengali and emoji
 * need them.
 */
export function visibleWording(value: string | null | undefined): string | null {
  const text = value?.trim() ?? '';
  return withoutHidden(text) === '' ? null : text;
}

/** The award's wording, from what it holds now, so a renamed award or program follows. */
export function awardLetterWording(award: {
  readonly letterWording: string | null | undefined;
  readonly programName: string | null | undefined;
  readonly title: string | null | undefined;
}): LetterWording {
  const entered = visibleWording(award.letterWording);
  if (entered !== null) return { text: entered, source: 'entered' };
  const title = visibleWording(award.title);
  if (title !== null) return { text: title, source: 'title' };
  const program = visibleWording(award.programName);
  if (program !== null) return { text: program, source: 'program' };
  return { text: null, source: 'none' };
}

/**
 * One person's letter wording: their own, saved on the line before APP-201 (so no letter already
 * made or reviewed changes under anyone), else the award's.
 */
export function lineLetterWording(
  own: string | null | undefined,
  award: LetterWording,
): string | null {
  return visibleWording(own) ?? award.text;
}
